<template>
  <Navigation withExit @back="emit('back')" @exit="emit('exit')" />
  <Title>{{ i18n(translations.title) }}</Title>

  <MessageForm :isLoading :mostUrgentTask @submit="sendMessage" />
</template>

<script setup lang="ts">
import { ref } from 'vue'

import { handleFileUpload } from '@backmarket/front-upload'
import { getHttpBaseUrl } from '@backmarket/nuxt-module-http/getHttpBaseUrl'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import type { ProductReturnState } from '~/api/care-folders/types/care-folder'
import type { OrderlineOpenTask } from '~/api/seller-after-sales/types'
import { ROUTE_NAMES } from '~/constants/routes'
import { fetchAndGoToNextTask } from '~/scopes/customer-care/components/request.helpers'
import { useFormatDeadline } from '~/scopes/customer-care/pages/customer-care/composables/useFormatDeadline'

import MessageForm from '../MessageForm/MessageForm.vue'
import Navigation from '../Navigation/Navigation.vue'
import Title from '../Title/Title.vue'

import translations from './SendMessageFlow.translations'

const i18n = useI18n()
const logger = useLogger()
const { openErrorToast, openSuccessToast } = useTheToast()
const $httpFetch = useHttpFetch()
const tracking = useTracking()
const emit = defineEmits(['back', 'done', 'exit'])

const { customerRequestId, mostUrgentTask, productReturnState } = defineProps<{
  customerRequestId: number
  mostUrgentTask?: OrderlineOpenTask
  productReturnState: ProductReturnState
}>()

const isLoading = ref(false)

async function sendMessage(values: {
  message: string
  attachments: File[]
  isNextTaskChecked: boolean
}) {
  isLoading.value = true
  try {
    const attachmentsPromises = values.attachments.map((file) =>
      handleFileUpload(
        `${getHttpBaseUrl()}/services/uploads`,
        'care_asp_attachment',
        { careFolderId: customerRequestId },
        file,
      ),
    )

    const uploadResponses = await Promise.all(attachmentsPromises)
    const isUploadSuccessful = uploadResponses.every(
      (res) => res?.status === 'success',
    )

    if (!isUploadSuccessful) {
      throw new Error('Some attachments failed to upload')
    }

    await $httpFetch(
      '/bm/customer_request/:customerRequestId/merchant/message',
      {
        method: 'POST',
        pathParams: {
          customerRequestId,
        },
        body: {
          kind: 'MERCHANT_TO_BACKCARE',
          message: values.message,
          attachmentHandles: uploadResponses.map((res) => res?.handle),
        },
      },
    )

    if (values.isNextTaskChecked)
      fetchAndGoToNextTask(mostUrgentTask?.id).catch(() => {
        openErrorToast()
      })

    tracking.trackFormSubmit({
      name: 'send-message',
      zone: 'asp-action-bar-send-message-step',
      customerRequestId,
      productReturnState,
      pageType: ROUTE_NAMES.CUSTOMER_CARE.CUSTOMER_REQUEST,
      mostUrgentDeadline: mostUrgentTask
        ? useFormatDeadline(mostUrgentTask.deadline, mostUrgentTask.id)
        : '',
      mostUrgentTaskType: mostUrgentTask?.status || '',
      goToNextTask: values.isNextTaskChecked,
    })

    emit('done')
    openSuccessToast()
  } catch (err) {
    openErrorToast()

    const error = err as Error
    logger.error(error.message, {
      error,
      owners: ['bot-squad-seller-care-front'],
    })
  } finally {
    isLoading.value = false
  }
}
</script>
